import { Check, Minus } from 'lucide-react'
import {
  Checkbox as CheckboxPrimitive,
  RadioGroup as RadioGroupPrimitive,
  Switch as SwitchPrimitive,
} from 'radix-ui'
import { type ComponentProps, type ReactNode, useId } from 'react'

import { cn } from '../lib/cn'
import { useFieldControl, useFormField } from './form-field'

/*
 * Controles de escolha. A borda do estado desmarcado usa ink-2 (contraste ≥ 3:1 exigido para
 * componentes de interface); ink-3 ficaria abaixo disso.
 */

/** Rótulo clicável ao lado do controle; herda o estado desabilitado pelo `peer`. */
function InlineLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="cursor-pointer text-body text-ink select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
    >
      {children}
    </label>
  )
}

export interface CheckboxProps extends ComponentProps<typeof CheckboxPrimitive.Root> {
  /** Rótulo ao lado da caixa. Sem ele, dê um aria-label ou use dentro de um FormField. */
  label?: ReactNode
  invalid?: boolean
}

export function Checkbox({
  label,
  invalid,
  className,
  id,
  disabled,
  'aria-describedby': describedBy,
  ...props
}: CheckboxProps) {
  const autoId = useId()
  const control = useFieldControl({ id, disabled, invalid, 'aria-describedby': describedBy })
  const checkboxId = control.id ?? autoId

  // Alvo de 24 × 24 (D3, WCAG 2.5.8) com o desenho de 18 px: a margem negativa mantém o espaço
  // ocupado no layout igual ao da caixa visível.
  const box = (
    <CheckboxPrimitive.Root
      id={checkboxId}
      disabled={control.disabled}
      {...control.ariaProps}
      className={cn(
        'peer group -m-[3px] grid size-6 shrink-0 place-items-center rounded-sm text-on-petroleo',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'grid size-[18px] place-items-center rounded-sm border border-ink-2 bg-surface',
          'group-data-[state=checked]:border-petroleo group-data-[state=checked]:bg-petroleo',
          'group-data-[state=indeterminate]:border-petroleo group-data-[state=indeterminate]:bg-petroleo',
          'group-aria-invalid:border-framboesa',
        )}
      >
        <CheckboxPrimitive.Indicator className="grid place-items-center">
          <Check aria-hidden className="size-3.5 group-data-[state=indeterminate]:hidden" />
          <Minus aria-hidden className="hidden size-3.5 group-data-[state=indeterminate]:block" />
        </CheckboxPrimitive.Indicator>
      </span>
    </CheckboxPrimitive.Root>
  )

  if (!label) return box
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {box}
      <InlineLabel htmlFor={checkboxId}>{label}</InlineLabel>
    </div>
  )
}

export interface SwitchProps extends ComponentProps<typeof SwitchPrimitive.Root> {
  label?: ReactNode
}

export function Switch({
  label,
  className,
  id,
  disabled,
  'aria-describedby': describedBy,
  ...props
}: SwitchProps) {
  const autoId = useId()
  const control = useFieldControl({ id, disabled, 'aria-describedby': describedBy })
  const switchId = control.id ?? autoId

  // Alvo de 44 × 28 (D3) com o trilho de 36 × 20; a margem negativa mantém o espaço ocupado.
  const track = (
    <SwitchPrimitive.Root
      id={switchId}
      disabled={control.disabled}
      aria-describedby={control.ariaProps['aria-describedby']}
      className={cn(
        'peer group -mx-1 -my-1 inline-flex h-7 w-11 shrink-0 items-center justify-center rounded-full',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className="inline-flex h-5 w-9 items-center rounded-full bg-ardosia group-data-[state=checked]:bg-petroleo"
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'block size-4 translate-x-0.5 rounded-full bg-white',
            'transition-transform duration-(--motion-fast) ease-state motion-reduce:transition-none',
            'data-[state=checked]:translate-x-[18px]',
          )}
        />
      </span>
    </SwitchPrimitive.Root>
  )

  if (!label) return track
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {track}
      <InlineLabel htmlFor={switchId}>{label}</InlineLabel>
    </div>
  )
}

export interface RadioGroupProps extends ComponentProps<typeof RadioGroupPrimitive.Root> {
  invalid?: boolean
}

/** Grupo de opções exclusivas. Dentro de um FormField, o rótulo do campo nomeia o grupo. */
export function RadioGroup({
  className,
  orientation = 'vertical',
  invalid,
  disabled,
  'aria-describedby': describedBy,
  ...props
}: RadioGroupProps) {
  const field = useFormField()
  const control = useFieldControl({ disabled, invalid, 'aria-describedby': describedBy })
  return (
    <RadioGroupPrimitive.Root
      orientation={orientation}
      disabled={control.disabled}
      aria-labelledby={field?.labelId}
      {...control.ariaProps}
      className={cn(
        'flex gap-2',
        orientation === 'horizontal' ? 'flex-row flex-wrap gap-x-4' : 'flex-col',
        className,
      )}
      {...props}
    />
  )
}

export interface RadioGroupItemProps extends ComponentProps<typeof RadioGroupPrimitive.Item> {
  label: ReactNode
}

export function RadioGroupItem({ label, className, id, ...props }: RadioGroupItemProps) {
  const autoId = useId()
  const itemId = id ?? autoId
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Alvo de 24 × 24 (D3) com o círculo de 18 px. */}
      <RadioGroupPrimitive.Item
        id={itemId}
        className={cn(
          'peer group -m-[3px] grid size-6 shrink-0 place-items-center rounded-full',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...props}
      >
        <span
          aria-hidden
          className="grid size-[18px] place-items-center rounded-full border border-ink-2 bg-surface group-data-[state=checked]:border-petroleo"
        >
          <RadioGroupPrimitive.Indicator className="block size-2 rounded-full bg-petroleo" />
        </span>
      </RadioGroupPrimitive.Item>
      <InlineLabel htmlFor={itemId}>{label}</InlineLabel>
    </div>
  )
}
